import { useCallback, useEffect, useReducer, useState } from "react"
import { AbstractConnector } from "@web3-react/abstract-connector"
import { createContainer } from "unstated-next"
import { useWeb3React, UnsupportedChainIdError } from "@web3-react/core"
import { useLocalStorage } from "../../hook/useLocalStorage"
import { STORAGE_KEY } from "../../constant/storage"
import { identify, reset } from "../../lib/segment/base"
import { XState } from "../xstate"
import { IS_MAINNET } from "../../constant"
import { useNotification } from "../../hook/useNotification"
import { usePrevious } from "../../hook/usePrevious"
import { SUPPORTED_WALLETS } from "constant/wallet"

enum ACTIONS {
    LOGIN_REQUEST = "LOGIN_REQUEST",
    LOGIN_SUCCESS = "LOGIN_SUCCESS",
    LOGIN_FAIL = "LOGIN_FAIL",
    LOGOUT = "LOGOUT",
}

type ActionType =
    | { type: ACTIONS.LOGIN_REQUEST }
    | { type: ACTIONS.LOGIN_SUCCESS; payload: { address: string } }
    | { type: ACTIONS.LOGIN_FAIL }
    | { type: ACTIONS.LOGOUT }

const initialState = {
    isLoading: false,
    address: "",
}

export const User = createContainer(useUser)

const { CONNECTOR_ID } = STORAGE_KEY

export const wrongNetworkError = {
    title: "Wrong network",
    description: `Please switch network to ${IS_MAINNET ? "ethereum mainnet" : "rinkeby"}.`,
    isClosable: true,
    duration: null,
}

function usePostLogout(onLogout: Function) {
    const { active } = useWeb3React()
    const previousSession = usePrevious(active)

    useEffect(() => {
        if (previousSession && !active) {
            // clear up state when users are logs out from wallet connect
            onLogout()
        }
    }, [previousSession, active, onLogout])
}

function useUser() {
    const [state, dispatch] = useReducer(reducer, initialState)
    const { active, account, activate, deactivate, chainId, error } = useWeb3React()
    const [, setConnectorId] = useLocalStorage(CONNECTOR_ID.name, CONNECTOR_ID.defaultValue)
    const { notifyError, closeNotify, closeAll } = useNotification()
    const {
        actions: { xDisconnectWallet },
    } = XState.useContainer()

    // NOTE: this will be called after user logout out
    usePostLogout(() => {
        setConnectorId("")
        dispatch({ type: ACTIONS.LOGOUT })
        xDisconnectWallet()
        reset()
    })

    const login = useCallback(
        (instance: AbstractConnector, connectorId: string, onActivate?: Function) => {
            dispatch({ type: ACTIONS.LOGIN_REQUEST })
            setConnectorId(connectorId)
            activate(instance, () => {}, true)
                .then(() => {
                    if (onActivate) {
                        onActivate()
                    }
                    console.log("connect success")
                    closeAll()
                })
                .catch(e => {
                    setConnectorId("")
                    dispatch({ type: ACTIONS.LOGIN_FAIL })
                    console.error("connect failed", e)
                    if (e instanceof UnsupportedChainIdError) {
                        notifyError(wrongNetworkError)
                    }
                })
        },
        [dispatch, setConnectorId, activate, closeAll, notifyError],
    )

    const logout = useCallback(() => {
        deactivate()
    }, [deactivate])

    useEffect(() => {
        if (active && account && chainId) {
            dispatch({ type: ACTIONS.LOGIN_SUCCESS, payload: { address: account } })
            identify(account)
        }
    }, [account, active, chainId, notifyError, closeNotify, error])

    // NOTE:
    // This hook is used to detect "chainChanged" event after user connected wallet
    useEffect(() => {
        const handleChainChanged = (chainId: string) => {
            const RINKEBY_CHAIN_ID = "0x4"
            const MAINNET_CHAIN_ID = "0x1"
            if (chainId !== (IS_MAINNET ? MAINNET_CHAIN_ID : RINKEBY_CHAIN_ID)) {
                notifyError(wrongNetworkError)
            } else {
                closeAll()
            }
        }
        if (active && account && chainId) {
            const { ethereum } = window
            if (ethereum?.on) {
                ethereum.on("chainChanged", handleChainChanged)
            }

            return () => {
                if (ethereum?.removeListener) {
                    ethereum.removeListener("chainChanged", handleChainChanged)
                }
            }
        }
    }, [account, active, chainId, notifyError, closeAll])

    // auto login
    const [isTried, setIsTried] = useState(false)
    const [connectorId] = useLocalStorage(CONNECTOR_ID.name, CONNECTOR_ID.defaultValue)
    useEffect(() => {
        const connector = SUPPORTED_WALLETS.find(walletInfo => walletInfo.id === connectorId)?.connector
        if (!isTried && connector) {
            console.log("auto login...")
            login(connector, connectorId)
            setIsTried(true)
        }
    }, [connectorId, isTried, login])

    return {
        state,
        actions: {
            login,
            logout,
        },
    }
}

function reducer(state: typeof initialState, action: ActionType) {
    switch (action.type) {
        case ACTIONS.LOGIN_REQUEST: {
            return { ...state, isLoading: true }
        }
        case ACTIONS.LOGIN_SUCCESS: {
            const { address } = action.payload
            return { ...state, isLoading: false, address }
        }
        case ACTIONS.LOGIN_FAIL: {
            return {
                ...state,
                isLoading: false,
            }
        }
        case ACTIONS.LOGOUT: {
            return {
                ...state,
                address: "",
            }
        }
        default:
            throw new Error()
    }
}
